import React from "react";
import { useNavigate } from "react-router-dom";

interface Plan {
  name: string;
  price: string;
  features: string[];
  popular: boolean;
}

const plans: Plan[] = [
  {
    name: "Free",
    price: "₦0",
    popular: false,
    features: [
      "1 User",
      "Up to 5 Items",
      "Basic Stock Tracking",
      "Email Support",
      "Community Access",
    ],
  },

  {
    name: "Starter (Lite)",
    price: "₦5,000",
    popular: false,
    features: [
      "1 Users",
      "30 Inventory Items (No Variants)",
      "Sales & Purchase",
      "Single Warehouse",
      "Low Level Support",
    ],
  },
  {
    name: "Basic",
    price: "₦10,000",
    popular: false,
    features: [
      "2 Users",
      "100 Inventory Items",
      "Sales & Purchase",
      "Multi Warehouse",
      "Medium Level Support",
    ],
  },
  {
    name: "Growth",
    price: "₦35,000",
    popular: true,
    features: [
      "4 Users",
      "1000 Inventory Items",
      "Advanced Reports",
      "Role Permissions",
      "24/7 Support",
    ],
  },
  {
    name: "Premium",
    price: "₦60,000",
    popular: false,
    features: [
      "10 Users",
      "Unlimited Inventory",
      "Custom Integrations",
      "Dedicated Manager",
      "Phone/Zoom/Whatapps Support",
    ],
  },
  {
    name: "Enterprise",
    price: "Let's Talk",
    popular: false,
    features: [
      "Unlimited Users",
      "Unlimited Inventory",
      "Hotels",
      "Hospitals",
      "Manufacturing",
      "Supermarkets & Malls",
    ],
  },
];

const InventoryPricingMiniComp = () => {

  const whatsappNumber = "2348073388263"; // Your WhatsApp Number
  const signupUrl = "https://admin.suparecords.ng/auth/signup";

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      `Hello Supa Records,

I'm interested in your Enterprise Plan.

Can you provide more information about pricing, setup, and available features?

Thank you.`
    );

    window.open(
      `https://wa.me/${whatsappNumber}?text=${message}`,
      "_blank"
    );
  };



  const openSignup = () => {
    window.open(
      signupUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <section className="relative overflow-hidden bg-slate-950 py-24">
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute left-0 top-0 h-96 w-96 rounded-full bg-red-500/20 blur-[140px]" />
        <div className="absolute right-0 bottom-0 h-96 w-96 rounded-full bg-blue-600/20 blur-[140px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">

        <div className="text-center max-w-3xl mx-auto mb-16">

          <span className="inline-flex rounded-full bg-red-500/10 px-5 py-2 text-sm font-semibold text-red-400 ring-1 ring-red-500/20">
            Pricing Plans
          </span>

          <h2 className="mt-6 text-5xl font-black tracking-tight text-white">
            Choose the plan that grows
            <span className="block text-red-500">
              with your business
            </span>
          </h2>

          <p className="mt-6 text-lg text-slate-400">
            Simple pricing with everything you need to manage inventory,
            warehouses, purchases, sales and reporting.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-2 xl:grid-cols-3">

          {plans.map((plan) => (

            <div
              key={plan.name}
              className={`group relative rounded-3xl border backdrop-blur-xl transition-all duration-500 hover:-translate-y-3 hover:shadow-2xl

                ${plan.popular
                  ? "border-red-500 bg-gradient-to-b from-red-500/15 to-slate-900 shadow-red-500/20 shadow-2xl scale-105"
                  : "border-white/10 bg-white/5 hover:border-red-500/40"
                }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="rounded-full bg-gradient-to-r from-red-600 to-red-500 px-5 py-2 text-xs font-bold uppercase tracking-widest text-white shadow-lg">
                    ★ Most Popular
                  </span>
                </div>
              )}

              <div className="p-8">

                <div className="mb-8">

                  <h3 className="text-2xl font-bold text-white">
                    {plan.name}
                  </h3>

                  <div className="mt-5 flex items-end">

                    <span className="text-5xl font-black text-white">
                      {plan.price}
                    </span>

                    {plan.price !== "Let's Talk" && (
                      <span className="ml-2 mb-2 text-slate-400">
                        /month
                      </span>
                    )}
                  </div>

                </div>

                <ul className="space-y-4">

                  {plan.features.map((feature) => (

                    <li
                      key={feature}
                      className="flex items-center text-slate-300"
                    >
                      <div className="mr-3 flex h-6 w-6 items-center justify-center rounded-full bg-red-500/15">

                        <svg
                          className="h-4 w-4 text-red-400"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>

                      </div>

                      {feature}

                    </li>

                  ))}

                </ul>

                <button
                  onClick={() => {
                    if (plan.price === "Let's Talk") {
                      openWhatsApp();
                    } else {
                      openSignup();
                    }
                  }}
                  className={`mt-10 w-full rounded-2xl py-4 font-semibold transition-all duration-300

    ${plan.popular
                      ? "bg-gradient-to-r from-red-600 to-red-500 text-white hover:shadow-lg hover:shadow-red-500/40"
                      : "bg-white/10 text-white hover:bg-white hover:text-slate-900"
                    }`}
                >
                  {plan.price === "Let's Talk"
                    ? "Contact Sales"
                    : "Get Started"}
                </button>

              </div>

            </div>

          ))}
        </div>
      </div>
    </section>
  );
};

export default InventoryPricingMiniComp;